/**
 * Turns an HttpErrorResponse from the local-auth endpoints into i18n keys.
 *
 * The server answers every refusal with a machine code in `error.errorCode`
 * (INVALID_CREDENTIALS, MFA_CODE_INVALID, PASSWORD_TOO_SHORT...) rather than a
 * sentence, and util/auth.interceptor.ts deliberately stays SILENT when a
 * response carries one -- the convention being that the component renders a
 * localized string instead of the toast showing raw server text.
 *
 * Password-policy refusals arrive comma-joined, because more than one rule can
 * fail at once, so this always returns an array.
 */

/** Codes are validated against this before being used to build a key. */
const CODE_PATTERN = /^[A-Z][A-Z0-9_]*$/;

const PREFIX = 'AUTH.';

export const AUTH_FALLBACK_KEY = PREFIX + 'UNEXPECTED';

export function authErrorKeys(err: any): string[] {
  const raw = err && err.error && err.error.errorCode;

  if (typeof raw === 'string' && raw.length > 0) {
    // Each code is pattern-checked before it becomes part of a translation key.
    // The value comes off the wire, and ngx-translate treats dots as a path into
    // the bundle -- so an unchecked value could be used to address an arbitrary
    // key, or simply render as raw noise to the user. Anything unrecognised
    // degrades to the generic message rather than being displayed.
    const keys = raw.split(',')
      .map((code: string) => code.trim())
      .filter((code: string) => CODE_PATTERN.test(code))
      .map((code: string) => PREFIX + code);
    if (keys.length) return keys;
    return [AUTH_FALLBACK_KEY];
  }

  // status 0 is the browser refusing to complete the request at all (offline,
  // DNS, CORS, blocked). It is not a server response and has no errorCode.
  if (err && err.status === 0) return [PREFIX + 'CONNECTION_FAILED'];

  // The rate limiters answer with their own body, but a proxy in front may
  // return a bare 429, so this is mapped explicitly rather than falling through
  // to "something went wrong" -- being told to slow down is actionable.
  if (err && err.status === 429) return [PREFIX + 'TOO_MANY_REQUESTS'];

  // 502/503/504 with no errorCode did not come from this application: it is the
  // load balancer answering while no task is healthy, which is what a redeploy
  // looks like from the browser. Saying so matters -- the alternative reads as
  // "something you did went wrong", and the person retypes a code that was
  // never checked. There is nothing to fix on their side but waiting.
  if (err && (err.status === 502 || err.status === 503 || err.status === 504)) {
    return [PREFIX + 'SERVER_UNAVAILABLE'];
  }

  return [AUTH_FALLBACK_KEY];
}
